import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Calculator, Download, RefreshCw, DollarSign, TrendingUp, TrendingDown,
  FileText, PieChart, AlertTriangle, CheckCircle, Clock, Building2,
  CreditCard, Landmark, Receipt, FileSpreadsheet, Bot
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Accounting = () => {
  const [summary, setSummary] = useState(null);
  const [taxReport, setTaxReport] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [syncStatus, setSyncStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [bankingStatus, setBankingStatus] = useState(null);
  const [llcs, setLlcs] = useState([]);
  const [selectedLlc, setSelectedLlc] = useState(null);
  const [llcTaxReport, setLlcTaxReport] = useState(null);
  const [depreciationSchedule, setDepreciationSchedule] = useState(null);
  const [capitalEvents, setCapitalEvents] = useState([]);

  useEffect(() => {
    fetchAllData();
    fetchLlcs();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [summaryRes, taxRes, txnRes, statusRes, bankRes] = await Promise.all([
        fetch(`${API_URL}/api/operations/accounting/summary`),
        fetch(`${API_URL}/api/operations/accounting/tax-report`),
        fetch(`${API_URL}/api/operations/accounting/transactions?limit=20`),
        fetch(`${API_URL}/api/operations/status`),
        fetch(`${API_URL}/api/operations/accounting/banking-status`)
      ]);

      if (summaryRes.ok) setSummary(await summaryRes.json());
      if (taxRes.ok) setTaxReport(await taxRes.json());
      if (txnRes.ok) {
        const data = await txnRes.json();
        setTransactions(data.transactions || []);
      }
      if (statusRes.ok) setSyncStatus(await statusRes.json());
      if (bankRes.ok) setBankingStatus(await bankRes.json());
    } catch (error) {
      console.error('Error fetching accounting data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLlcs = async () => {
    try {
      const res = await fetch(`${API_URL}/api/llcs`);
      if (res.ok) {
        const data = await res.json();
        setLlcs(data);
      }
    } catch (error) {
      console.error('Error fetching LLCs:', error);
    }
  };

  const fetchLlcTaxReport = async (llcId) => {
    setSelectedLlc(llcId);
    try {
      const [reportRes, depRes, eventsRes] = await Promise.all([
        fetch(`${API_URL}/api/tax/llc/${llcId}/report`),
        fetch(`${API_URL}/api/tax/depreciation/schedule?llc_id=${llcId}`),
        fetch(`${API_URL}/api/tax/capital-events?llc_id=${llcId}`)
      ]);
      if (reportRes.ok) setLlcTaxReport(await reportRes.json());
      if (depRes.ok) setDepreciationSchedule(await depRes.json());
      if (eventsRes.ok) setCapitalEvents((await eventsRes.json()).events || []);
    } catch (error) {
      console.error('Error fetching LLC tax report:', error);
    }
  };

  const exportLlcReport = async (llcId, format) => {
    const url = `${API_URL}/api/tax/export/llc/${llcId}/${format}`;
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error('Export failed');
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `llc_tax_report_${llcId}.${format === 'pdf' ? 'pdf' : 'csv'}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Export error:', error);
    }
  };

  const triggerSync = async () => {
    setSyncing(true);
    try {
      const res = await fetch(`${API_URL}/api/operations/sync?bot_type=accounting`, { method: 'POST' });
      if (res.ok) {
        await fetchAllData();
      }
    } catch (error) {
      console.error('Sync error:', error);
    } finally {
      setSyncing(false);
    }
  };

  const exportReport = async (type, format) => {
    const url = type === 'tax' 
      ? `${API_URL}/api/operations/export/tax/${format}`
      : `${API_URL}/api/operations/export/portfolio/${format}`;
    
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error('Export failed');
      
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      
      // Get filename from Content-Disposition header or generate one
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `${type}_report.${format === 'excel' ? 'xlsx' : format === 'word' ? 'docx' : 'pdf'}`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename=([^;]+)/);
        if (filenameMatch) filename = filenameMatch[1].replace(/"/g, '');
      }
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Export error:', error);
      alert('Failed to export report. Please try again.');
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      rent_income: 'bg-green-500',
      other_income: 'bg-emerald-500',
      mortgage_interest: 'bg-blue-500',
      property_tax: 'bg-purple-500',
      insurance: 'bg-indigo-500',
      repairs: 'bg-orange-500',
      utilities: 'bg-yellow-500',
      capital_improvement: 'bg-red-500',
      uncategorized: 'bg-gray-500'
    };
    return colors[category] || 'bg-gray-500';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Accounting & Tax</h1>
          <p className="text-muted-foreground">Automated bookkeeping, tax preparation, and financial reports</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchAllData}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={triggerSync} disabled={syncing}>
            {syncing ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Bot className="h-4 w-4 mr-2" />}
            {syncing ? 'Syncing...' : 'Sync Now'}
          </Button>
        </div>
      </div>

      {/* Bot Status Card */}
      <Card className="border-l-4 border-l-green-500">
        <CardContent className="pt-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 rounded-full">
                <Calculator className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <h3 className="font-semibold">Accounting & Tax Bot</h3>
                <p className="text-sm text-muted-foreground">
                  {syncStatus?.accounting_bot?.sync_in_progress 
                    ? 'Sync in progress...' 
                    : `Last sync: ${syncStatus?.scheduler?.last_accounting_sync 
                        ? new Date(syncStatus.scheduler.last_accounting_sync).toLocaleString() 
                        : 'Never'}`
                  }
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant={syncStatus?.accounting_bot?.autonomous_lifecycle ? 'default' : 'secondary'}>
                {syncStatus?.accounting_bot?.current_state || 'idle'}
              </Badge>
              <Badge variant="outline" className="text-green-600">
                Hourly Sync @ :05
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Banking Integration Status */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Landmark className="h-5 w-5" />
              Banking Integration (Plaid)
            </CardTitle>
            <Badge variant={bankingStatus?.status === 'mocked' ? 'secondary' : 'default'}>
              {bankingStatus?.status || 'Not Connected'}
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {bankingStatus?.connected_accounts?.map((account, idx) => (
              <div key={idx} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <div className="flex items-center gap-3">
                  <CreditCard className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="font-medium">{account.name}</p>
                    <p className="text-sm text-muted-foreground">{account.type}</p>
                  </div>
                </div>
                <span className="font-semibold">${account.balance?.toLocaleString()}</span>
              </div>
            ))}
          </div>
          {bankingStatus?.status === 'mocked' && (
            <p className="text-sm text-muted-foreground mt-4">
              <AlertTriangle className="h-4 w-4 inline mr-1" />
              Banking integration is mocked. Add Plaid credentials to enable live sync.
            </p>
          )}
        </CardContent>
      </Card>

      <Tabs defaultValue="summary" className="space-y-4">
        <TabsList className="flex-wrap">
          <TabsTrigger value="summary">Summary</TabsTrigger>
          <TabsTrigger value="transactions">Transactions</TabsTrigger>
          <TabsTrigger value="tax">Tax Report</TabsTrigger>
          <TabsTrigger value="llc-taxes">LLC Tax Reports</TabsTrigger>
          <TabsTrigger value="depreciation">Depreciation</TabsTrigger>
          <TabsTrigger value="capital-events">Capital Events</TabsTrigger>
          <TabsTrigger value="export">Export</TabsTrigger>
        </TabsList>

        {/* Summary Tab */}
        <TabsContent value="summary" className="space-y-4">
          {/* KPI Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Income</p>
                    <p className="text-2xl font-bold text-green-600">
                      ${summary?.total_income?.toLocaleString() || '0'}
                    </p>
                  </div>
                  <TrendingUp className="h-8 w-8 text-green-500" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Expenses</p>
                    <p className="text-2xl font-bold text-red-600">
                      ${summary?.total_expenses?.toLocaleString() || '0'}
                    </p>
                  </div>
                  <TrendingDown className="h-8 w-8 text-red-500" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Net Income</p>
                    <p className={`text-2xl font-bold ${(summary?.net_income || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      ${summary?.net_income?.toLocaleString() || '0'}
                    </p>
                  </div>
                  <DollarSign className="h-8 w-8 text-primary" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">NOI</p>
                    <p className="text-2xl font-bold text-blue-600">
                      ${summary?.noi?.toLocaleString() || '0'}
                    </p>
                  </div>
                  <PieChart className="h-8 w-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Property Breakdown */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Property Breakdown</CardTitle>
              <CardDescription>Income and expenses by property</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {summary?.property_breakdown?.map((prop, idx) => (
                  <div key={idx} className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center gap-3">
                      <Building2 className="h-5 w-5 text-muted-foreground" />
                      <span className="font-medium">{prop.property_id}</span>
                    </div>
                    <div className="flex items-center gap-6 text-sm">
                      <div>
                        <span className="text-muted-foreground">Income: </span>
                        <span className="text-green-600 font-medium">${prop.income?.toLocaleString()}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Expenses: </span>
                        <span className="text-red-600 font-medium">${prop.expenses?.toLocaleString()}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Net: </span>
                        <span className={`font-medium ${prop.net_income >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          ${prop.net_income?.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Transactions Tab */}
        <TabsContent value="transactions">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Recent Transactions</CardTitle>
              <CardDescription>Auto-categorized from bank sync</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {transactions.map((txn, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50">
                    <div className="flex items-center gap-3">
                      <div className={`w-2 h-2 rounded-full ${getCategoryColor(txn.category)}`} />
                      <div>
                        <p className="font-medium">{txn.description}</p>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <span>{new Date(txn.date).toLocaleDateString()}</span>
                          <Badge variant="outline" className="text-xs">{txn.category || 'uncategorized'}</Badge>
                          {txn.is_deductible && (
                            <Badge variant="secondary" className="text-xs">Deductible</Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    <span className={`font-semibold ${txn.amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {txn.amount >= 0 ? '+' : ''}{txn.amount?.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                    </span>
                  </div>
                ))}
                {transactions.length === 0 && (
                  <p className="text-center text-muted-foreground py-8">No transactions found. Sync to fetch new data.</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tax Report Tab */}
        <TabsContent value="tax" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Tax Report - {taxReport?.year}</CardTitle>
                  <CardDescription>IRS-ready tax summary (Schedule E)</CardDescription>
                </div>
                <Badge variant="outline" className="text-green-600">
                  <CheckCircle className="h-3 w-3 mr-1" />
                  Ready for CPA
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Income */}
                <div className="space-y-3">
                  <h4 className="font-semibold text-green-600 flex items-center gap-2">
                    <TrendingUp className="h-4 w-4" /> Income
                  </h4>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Rental Income</span>
                      <span>${taxReport?.income?.rental_income?.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Other Income</span>
                      <span>${taxReport?.income?.other_income?.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between font-semibold pt-2 border-t">
                      <span>Total Income</span>
                      <span className="text-green-600">${taxReport?.income?.total_income?.toLocaleString()}</span>
                    </div>
                  </div>
                </div>

                {/* Expenses */}
                <div className="space-y-3">
                  <h4 className="font-semibold text-red-600 flex items-center gap-2">
                    <TrendingDown className="h-4 w-4" /> Deductible Expenses
                  </h4>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Mortgage Interest</span>
                      <span>${taxReport?.expenses?.mortgage_interest?.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Property Taxes</span>
                      <span>${taxReport?.expenses?.property_taxes?.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Insurance</span>
                      <span>${taxReport?.expenses?.insurance?.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Repairs</span>
                      <span>${taxReport?.expenses?.repairs_maintenance?.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between font-semibold pt-2 border-t">
                      <span>Total Expenses</span>
                      <span className="text-red-600">${taxReport?.expenses?.total_expenses?.toLocaleString()}</span>
                    </div>
                  </div>
                </div>

                {/* Summary */}
                <div className="space-y-3">
                  <h4 className="font-semibold text-blue-600 flex items-center gap-2">
                    <Calculator className="h-4 w-4" /> Summary
                  </h4>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Depreciation</span>
                      <span>${taxReport?.depreciation?.total_depreciation?.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between font-bold text-lg pt-2 border-t">
                      <span>Net Taxable</span>
                      <span className="text-blue-600">${taxReport?.net_income?.toLocaleString()}</span>
                    </div>
                  </div>
                  <div className="pt-4">
                    <p className="text-sm text-muted-foreground mb-2">Forms Needed:</p>
                    <div className="flex flex-wrap gap-1">
                      {taxReport?.forms_needed?.map((form, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs">{form}</Badge>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* LLC Tax Reports Tab */}
        <TabsContent value="llc-taxes" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* LLC Selector */}
            <Card className="md:col-span-1">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Your LLCs</CardTitle>
                <CardDescription>Select an LLC to view tax report</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                {llcs.map(llc => (
                  <div 
                    key={llc.id}
                    className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                      selectedLlc === llc.id 
                        ? 'border-primary bg-primary/10' 
                        : 'border-border hover:border-primary/50'
                    }`}
                    onClick={() => fetchLlcTaxReport(llc.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{llc.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {llc.tax_classification?.replace('_', ' ')}
                        </p>
                      </div>
                      <Badge variant="outline">{llc.property_count || 0}</Badge>
                    </div>
                  </div>
                ))}
                {llcs.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No LLCs found. Add LLCs from Admin settings.
                  </p>
                )}
              </CardContent>
            </Card>

            {/* LLC Tax Report */}
            <Card className="md:col-span-2">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">
                    {llcTaxReport?.llc_name || 'Select an LLC'}
                  </CardTitle>
                  {llcTaxReport && (
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => exportLlcReport(selectedLlc, 'pdf')}>
                        <Download className="h-4 w-4 mr-1" /> PDF
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => exportLlcReport(selectedLlc, 'excel')}>
                        <Download className="h-4 w-4 mr-1" /> Excel
                      </Button>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {llcTaxReport ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="p-3 bg-green-500/10 rounded-lg">
                        <p className="text-xs text-muted-foreground">Total Income</p>
                        <p className="text-xl font-bold text-green-600">
                          ${llcTaxReport.income?.gross_income?.toLocaleString()}
                        </p>
                      </div>
                      <div className="p-3 bg-red-500/10 rounded-lg">
                        <p className="text-xs text-muted-foreground">Total Expenses</p>
                        <p className="text-xl font-bold text-red-600">
                          ${llcTaxReport.expenses?.total_operating_expenses?.toLocaleString()}
                        </p>
                      </div>
                      <div className="p-3 bg-blue-500/10 rounded-lg">
                        <p className="text-xs text-muted-foreground">Depreciation</p>
                        <p className="text-xl font-bold text-blue-600">
                          ${llcTaxReport.depreciation?.total_annual_depreciation?.toLocaleString()}
                        </p>
                      </div>
                      <div className="p-3 bg-purple-500/10 rounded-lg">
                        <p className="text-xs text-muted-foreground">Taxable Income</p>
                        <p className="text-xl font-bold text-purple-600">
                          ${llcTaxReport.summary?.taxable_income?.toLocaleString()}
                        </p>
                      </div>
                    </div>

                    <div className="border-t pt-4">
                      <h4 className="font-semibold mb-2">Property Breakdown</h4>
                      <div className="space-y-2">
                        {llcTaxReport.property_breakdown?.map((prop, idx) => (
                          <div key={idx} className="flex items-center justify-between p-2 bg-muted rounded">
                            <span className="text-sm">{prop.address}</span>
                            <div className="flex gap-4 text-sm">
                              <span className="text-green-600">${prop.income?.toLocaleString()}</span>
                              <span className="text-red-600">-${prop.expenses?.toLocaleString()}</span>
                              <span className="font-semibold">${prop.noi?.toLocaleString()}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="border-t pt-4">
                      <p className="text-sm text-muted-foreground">Required Tax Forms:</p>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {llcTaxReport.forms_required?.map((form, idx) => (
                          <Badge key={idx} variant="outline">{form}</Badge>
                        ))}
                      </div>
                    </div>

                    {llcTaxReport.k1_data && (
                      <div className="border-t pt-4">
                        <h4 className="font-semibold mb-2 flex items-center gap-2">
                          <FileText className="h-4 w-4" /> K-1 Ready Data
                        </h4>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div className="flex justify-between">
                            <span>Ordinary Business Income:</span>
                            <span>${llcTaxReport.k1_data.ordinary_business_income?.toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Rental Income:</span>
                            <span>${llcTaxReport.k1_data.rental_income?.toLocaleString()}</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    Select an LLC to view consolidated tax report
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Depreciation Schedule Tab */}
        <TabsContent value="depreciation" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Depreciation Schedules</CardTitle>
                  <CardDescription>27.5-year straight-line depreciation for all properties</CardDescription>
                </div>
                <Button variant="outline" onClick={() => {
                  fetch(`${API_URL}/api/tax/depreciation/schedule`)
                    .then(r => r.json())
                    .then(setDepreciationSchedule);
                }}>
                  <RefreshCw className="h-4 w-4 mr-2" /> Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {depreciationSchedule?.schedules?.length > 0 ? (
                <div className="space-y-4">
                  <div className="p-4 bg-blue-500/10 rounded-lg text-center">
                    <p className="text-sm text-muted-foreground">Total Annual Depreciation</p>
                    <p className="text-3xl font-bold text-blue-600">
                      ${depreciationSchedule.total_annual_depreciation?.toLocaleString()}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Across {depreciationSchedule.property_count} properties
                    </p>
                  </div>
                  
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left p-2">Property</th>
                          <th className="text-left p-2">LLC</th>
                          <th className="text-right p-2">Building Value</th>
                          <th className="text-right p-2">Annual Depr.</th>
                          <th className="text-right p-2">Accumulated</th>
                          <th className="text-right p-2">Remaining Basis</th>
                        </tr>
                      </thead>
                      <tbody>
                        {depreciationSchedule.schedules.map((item, idx) => (
                          <tr key={idx} className="border-b">
                            <td className="p-2">{item.address}</td>
                            <td className="p-2 text-muted-foreground">{item.llc_name || 'Unassigned'}</td>
                            <td className="p-2 text-right">${item.building_value?.toLocaleString()}</td>
                            <td className="p-2 text-right text-blue-600">${item.annual_depreciation?.toLocaleString()}</td>
                            <td className="p-2 text-right">${item.accumulated_depreciation?.toLocaleString()}</td>
                            <td className="p-2 text-right">${item.remaining_basis?.toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  No depreciation data available. Add properties to see schedules.
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Capital Events Tab */}
        <TabsContent value="capital-events" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Capital Events</CardTitle>
                  <CardDescription>Property sales, refinances, and 1031 exchanges</CardDescription>
                </div>
                <Button variant="outline" onClick={() => {
                  fetch(`${API_URL}/api/tax/capital-events`)
                    .then(r => r.json())
                    .then(data => setCapitalEvents(data.events || []));
                }}>
                  <RefreshCw className="h-4 w-4 mr-2" /> Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {capitalEvents.length > 0 ? (
                <div className="space-y-4">
                  {capitalEvents.map((event, idx) => (
                    <div key={idx} className="p-4 border rounded-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{event.property_address}</p>
                          <p className="text-sm text-muted-foreground">{event.event_date}</p>
                        </div>
                        <div className="text-right">
                          <Badge className={
                            event.event_type === 'sale' ? 'bg-green-500' :
                            event.event_type === 'refinance' ? 'bg-blue-500' :
                            'bg-purple-500'
                          }>
                            {event.event_type}
                          </Badge>
                          {event.gain && (
                            <p className="text-lg font-bold text-green-600 mt-1">
                              +${event.gain?.toLocaleString()}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  No capital events recorded. Record property sales or refinances here.
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Export Tab */}
        <TabsContent value="export">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Portfolio Export */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Building2 className="h-5 w-5" />
                  Portfolio Report
                </CardTitle>
                <CardDescription>Export portfolio summary and KPIs</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col gap-2">
                  <Button variant="outline" onClick={() => exportReport('portfolio', 'excel')} className="justify-start">
                    <FileSpreadsheet className="h-4 w-4 mr-2 text-green-600" />
                    Download Excel (.xlsx)
                  </Button>
                  <Button variant="outline" onClick={() => exportReport('portfolio', 'word')} className="justify-start">
                    <FileText className="h-4 w-4 mr-2 text-blue-600" />
                    Download Word (.docx)
                  </Button>
                  <Button variant="outline" onClick={() => exportReport('portfolio', 'pdf')} className="justify-start">
                    <Receipt className="h-4 w-4 mr-2 text-red-600" />
                    Download PDF
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Tax Export */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Calculator className="h-5 w-5" />
                  Tax Report
                </CardTitle>
                <CardDescription>IRS-ready and CPA-friendly formats</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col gap-2">
                  <Button variant="outline" onClick={() => exportReport('tax', 'excel')} className="justify-start">
                    <FileSpreadsheet className="h-4 w-4 mr-2 text-green-600" />
                    IRS-Ready Excel (.xlsx)
                  </Button>
                  <Button variant="outline" onClick={() => exportReport('tax', 'word')} className="justify-start">
                    <FileText className="h-4 w-4 mr-2 text-blue-600" />
                    CPA-Friendly Word (.docx)
                  </Button>
                  <Button variant="outline" onClick={() => exportReport('tax', 'pdf')} className="justify-start">
                    <Receipt className="h-4 w-4 mr-2 text-red-600" />
                    Summary PDF
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Accounting;
